/**
 * Global "connection lost" banner (#243), ported from the TurboTwin
 * webclient's ConnectionErrorAlert.
 *
 * Before this, a dropped socket was invisible: pages rendered the empty
 * cache as "no data" (the orientation editor said "No orientations on this
 * site yet") and the first write failed with "Not connected to MineTwin
 * module". The reconnect hooks were healing it the whole time.
 *
 * Waits 15 s before saying anything: most drops are edge resets that heal in
 * a few seconds, and a banner flashing several times an hour helps nobody.
 * Lists which modules are down and why, with Retry (reconnect now) and, for a
 * sign-in problem, Re-authenticate.
 */
import { useEffect, useRef, useState } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { useAuthKit } from '@/contexts/AuthProvider';
import { requestReconnectNow } from '@/services/connectionManager/useAutoReconnect';
import { bannerActions, downModules, type ModuleState } from '@/lib/connectionStatus';

const GRACE_MS = 15_000;

type Key = 'minetwin' | 'org' | 'core' | 'mp' | 'assets' | 'launcher';

const LABELS: Record<Key, string> = {
  minetwin: 'Orientations and people',
  org: 'Organization and sites',
  core: 'Site data',
  mp: 'Live sessions',
  assets: '3D models',
  launcher: 'Desktop app launcher',
};

function useModuleStates(hasSite: boolean): ModuleState[] {
  const states: Record<Key, ReturnType<typeof useSpacetimeDB>> = {
    minetwin: useSpacetimeDB('minetwin'),
    org: useSpacetimeDB('org'),
    core: useSpacetimeDB('core'),
    mp: useSpacetimeDB('mp'),
    assets: useSpacetimeDB('assets'),
    launcher: useSpacetimeDB('launcher'),
  };
  // Latch per module: a module still on its first connect is loading, not down.
  const ever = useRef<Partial<Record<Key, boolean>>>({});
  return (Object.keys(LABELS) as Key[])
    // With no site the core connection dials a placeholder that fails on purpose.
    .filter((k) => k !== 'core' || hasSite)
    .map((k) => {
      const st = states[k];
      if (st.isActive) ever.current[k] = true;
      return {
        id: k,
        label: LABELS[k],
        isActive: st.isActive,
        error: st.connectionError ? st.connectionError.message || String(st.connectionError) : null,
        everActive: !!ever.current[k],
      };
    });
}

export function ConnectionBanner({ hasSite }: { hasSite: boolean }) {
  const { signIn } = useAuthKit();
  const down = downModules(useModuleStates(hasSite));
  const downKey = down.map((m) => m.id).join(',');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!downKey) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), GRACE_MS);
    return () => clearTimeout(t);
  }, [downKey]);

  if (!downKey || !visible) return null;
  const { showRetry, showReauth } = bannerActions(down, false);

  return (
    <div
      role="alert"
      style={{
        margin: '10px 18px 0',
        padding: '10px 14px',
        border: '1px solid var(--bad-line, var(--bad))',
        borderRadius: 'var(--r, 8px)',
        background: 'var(--panel)',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, fontSize: 12.5 }}>
        <b style={{ color: 'var(--bad)' }}>Connection lost: reconnecting automatically.</b>{' '}
        <span style={{ color: 'var(--muted)' }}>
          Lists may look empty and saves will wait until it is back. Nothing has been deleted.
        </span>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--muted)', fontSize: 11.5 }}>
          {down.map((m) => (
            <li key={m.id}>
              <span style={{ color: 'var(--text)' }}>{m.label}</span>
              {m.explanation ? `: ${m.explanation}` : m.error ? `: ${m.error.slice(0, 140)}` : ': disconnected'}
            </li>
          ))}
        </ul>
      </div>
      {showReauth ? (
        <button type="button" className="btn sm" onClick={() => void signIn()}>
          Sign in again
        </button>
      ) : null}
      {showRetry ? (
        <button type="button" className="btn sm primary" onClick={requestReconnectNow}>
          Retry now
        </button>
      ) : null}
    </div>
  );
}
